/**
 * SettingsPage — Supervisor settings.
 * Break start delay + warnings before capture — all apply to operators under this supervisor.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Card, CardContent, CircularProgress,
  FormControlLabel, Slider, Stack, Switch, Typography,
  TextField,
} from '@mui/material';
import AccessTimeIcon  from '@mui/icons-material/AccessTime';
import PublicIcon      from '@mui/icons-material/Public';
import WarningIcon     from '@mui/icons-material/Warning';
import SaveIcon        from '@mui/icons-material/Save';
import api, { getTimezones, setTimezone as apiSetTimezone } from '../api/client';
import { isSupervisor, isAdmin } from '../api/client';
import { useTimezoneContext } from '../hooks/useTimezone';

/**
 * General Settings → Timezone.
 *
 * Vendor-admin/infra-admin can edit; supervisor is read-only; operators
 * never get a dashboard login so no explicit gate is needed for them.
 * Backend enforces the same RBAC independently (PUT /settings/timezone
 * requires role=administrator, rejects super_administrator) — see
 * server/api/workforce.py's set_timezone.
 */
function GeneralSettingsCard({ readOnly }) {
  const { timezone: currentTimezone, loading: tzLoading, refresh } = useTimezoneContext();
  const [options, setOptions] = useState([]);
  const [value, setValue] = useState(currentTimezone);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { setValue(currentTimezone); }, [currentTimezone]);
  useEffect(() => {
    getTimezones().then(d => setOptions(d.timezones || [])).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true); setSuccess(''); setError('');
    try {
      await apiSetTimezone(value);
      setSuccess('Timezone updated — every page now displays times in this zone.');
      refresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to update timezone');
    } finally { setSaving(false); }
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} mb={1}>
          <PublicIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>General Settings — Timezone</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {readOnly
            ? 'The display timezone used across the dashboard. Only an admin can change this.'
            : 'Every page — Presence, Sessions, Reports, Events, Audit Logs, exports — displays times in this timezone. Data is always stored in UTC; this only controls how it\'s shown.'}
        </Typography>
        {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {tzLoading ? <CircularProgress size={24} /> : readOnly ? (
          <TextField label="Timezone" value={currentTimezone} size="small" sx={{ minWidth: 320 }}
            InputProps={{ readOnly: true }} />
        ) : (
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Autocomplete
              options={options}
              value={value}
              onChange={(_, v) => setValue(v || 'Asia/Kolkata')}
              disableClearable
              sx={{ minWidth: 320 }}
              renderInput={(params) => <TextField {...params} label="Timezone" size="small" />}
            />
            <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving || !value}>
              {saving ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

const WARN_TYPES = [
  { key: 'drowsiness', label: 'Drowsiness',  emoji: '😴', color: '#f44336',
    desc: 'Eyes closing / head drooping' },
  { key: 'yawning',   label: 'Yawning',     emoji: '🥱', color: '#ff9800',
    desc: 'Mouth opening wide' },
  { key: 'distracted',label: 'Distracted',  emoji: '👀', color: '#9c27b0',
    desc: 'Head turned away from camera' },
];
const PRESENCE_TYPE = { key: 'presence', label: 'Presence Monitoring', emoji: '📍',
  color: '#00897b', desc: 'Measures scheduled-shift presence and away sessions' };
const DETECTION_TYPES = [...WARN_TYPES, PRESENCE_TYPE];

const DEFAULT_DETECTIONS = Object.fromEntries(
  DETECTION_TYPES.map(type => [type.key, type.key === 'presence'
    ? { enabled: true, sensitivity: 5, away_threshold_minutes: 3,
        auto_close_at_shift_end: true }
    : { enabled: true, sensitivity: 5 }])
);

function WarningSlider({ type, value, onChange, disabled }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography fontSize="1.2rem">{type.emoji}</Typography>
          <Box>
            <Typography variant="body2" fontWeight={600}>{type.label}</Typography>
            <Typography variant="caption" color="text.secondary">{type.desc}</Typography>
          </Box>
        </Stack>
        <Typography variant="h5" fontWeight={700} sx={{ color: type.color }}>
          {value === 0 ? 'Capture immediately' : `${value} warning${value !== 1 ? 's' : ''}`}
        </Typography>
      </Stack>
      <Slider
        disabled={disabled}
        value={value}
        onChange={(_, v) => onChange(v)}
        min={0} max={10} step={1}
        marks={[
          { value: 0,  label: '0 (instant)' },
          { value: 3,  label: '3' },
          { value: 5,  label: '5' },
          { value: 10, label: '10' },
        ]}
        sx={{ color: type.color }}
        valueLabelDisplay="auto"
      />
      <Typography variant="caption" color="text.secondary">
        {disabled ? `${type.label} detection is disabled; warnings are not used.` : value === 0
          ? `Every ${type.label.toLowerCase()} episode is captured immediately.`
          : `First ${value} ${type.label.toLowerCase()} episode${value !== 1 ? 's' : ''} show a warning to the operator. After that, episodes are captured and stored.`}
      </Typography>
    </Box>
  );
}

export default function SettingsPage() {
  const [delay,    setDelay]    = useState(1);
  const [warnings, setWarnings] = useState({
    drowsiness: 2,
    yawning:    3,
    distracted: 2,
  });
  const [detections, setDetections] = useState(DEFAULT_DETECTIONS);
  const [loading,  setLoading]  = useState(false);
  const [saving,   setSaving]   = useState(false);
  const [success,  setSuccess]  = useState('');
  const [error,    setError]    = useState('');
  const [featurePermissions, setFeaturePermissions] = useState(null);

  useEffect(() => {
    if (!isSupervisor()) return;
    setLoading(true);
    api.get('/api/v1/supervisor/settings')
      .then(r => {
        const d = r.data;
        setDelay(d.break_start_delay_minutes ?? 1);
        setWarnings({
          drowsiness: d.warnings_before_capture_drowsiness ?? 2,
          yawning:    d.warnings_before_capture_yawning    ?? 3,
          distracted: d.warnings_before_capture_distracted ?? 2,
        });
        if (d.detection_settings) {
          // Detection types are always shown now — enablement lives solely in
          // Alarm Configuration (vendor-admin-owned); this page only shows
          // the current on/off state read-only alongside sensitivity, which
          // supervisors do still own.
          setFeaturePermissions(Object.fromEntries(DETECTION_TYPES.map(type => [type.key, true])));
          setDetections(values => Object.fromEntries(
          DETECTION_TYPES.map(type => [type.key, {
            ...d.detection_settings[type.key],
            enabled: Boolean(d.detection_settings[type.key]?.enabled),
          }])
          ));
        }
      })
      .catch(e => setError(e.response?.data?.detail || 'Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true); setSuccess(''); setError('');
    try {
      await api.put('/api/v1/supervisor/settings', {
        break_start_delay_minutes:           delay,
        warnings_before_capture_drowsiness:  warnings.drowsiness,
        warnings_before_capture_yawning:     warnings.yawning,
        warnings_before_capture_distracted:  warnings.distracted,
      });
      await api.put('/api/v1/supervisor/detection-settings', detections);
      setSuccess('Settings saved — active edge devices refresh detection settings within about one minute.');
    } catch (e) {
      setError(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (isAdmin()) return (
    <Box sx={{ p: 3, maxWidth: 700 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Settings</Typography>
      <Stack spacing={3}>
        <GeneralSettingsCard readOnly={false} />
        <Alert severity="info">
          Break delay and warning counts are configured per supervisor.
          Go to <strong>Operators</strong>, edit a supervisor, and these settings apply
          to all operators under that supervisor.
        </Alert>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 700 }}>
      <Typography variant="h5" fontWeight={700} mb={1}>Supervisor Settings</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        These settings apply to <strong>all operators under you</strong>. Changes take
        refresh automatically on active edge devices within about one minute.
      </Typography>

      {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? <CircularProgress /> : (
        <Stack spacing={3}>

          <GeneralSettingsCard readOnly />

          {/* Break delay */}
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <AccessTimeIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>Break Start Delay</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={2}>
                How long after you approve a break before it starts. Gives operators
                time to wrap up their current task.
              </Typography>
              <Stack direction="row" justifyContent="space-between" mb={1}>
                <Typography variant="body2" color="text.secondary">Delay</Typography>
                <Typography variant="h5" fontWeight={700} color="primary">
                  {delay === 0 ? 'Immediate' : `${delay} min`}
                </Typography>
              </Stack>
              <Box sx={{ px: 1 }}>
                <Slider
                  value={delay} onChange={(_, v) => setDelay(v)}
                  min={0} max={15} step={1}
                  marks={[
                    { value: 0,  label: '0' },
                    { value: 5,  label: '5 min' },
                    { value: 10, label: '10 min' },
                    { value: 15, label: '15 min' },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
            </CardContent>
          </Card>

          {/* Warnings before capture */}
          <Card>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <WarningIcon sx={{ color: '#ff9800' }} />
                <Typography variant="h6" fontWeight={600}>Warnings Before Capture</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" mb={3}>
                When a detection event fires, show this many warnings to the operator
                first before actually capturing and storing the event. The operator
                sees a countdown on their screen. Set to 0 to capture immediately.
              </Typography>

              <Alert severity="info" sx={{ mb: 3, py: 0.5 }}>
                <strong>How it works:</strong> If warnings = 3 for drowsiness, the first 3
                drowsiness episodes show "⚠ 2 warnings left", "⚠ 1 warning left", "⚠ Last warning"
                on the operator's screen. The 4th episode is captured and sent to the dashboard.
                Warnings reset when the operator logs out.
              </Alert>

              <Box sx={{ px: 1 }}>
                {WARN_TYPES.filter(t => featurePermissions && featurePermissions[`${t.key}`]).map(t => (
                  <WarningSlider
                    key={t.key}
                    type={t}
                    value={warnings[t.key]}
                    onChange={v => setWarnings(w => ({ ...w, [t.key]: v }))}
                    disabled={!detections[t.key].enabled}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={1}>Detection Settings</Typography>
              <Typography variant="body2" color="text.secondary" mb={3}>
                Choose how easily each detector triggers. Changes refresh
                automatically on active edge devices within about one minute.
                Turning a detector on or off is managed by your vendor
                administrator in Alarm Configuration.
              </Typography>
              <Stack spacing={3}>
                {DETECTION_TYPES.filter(type => featurePermissions && featurePermissions[type.key]).map(type => {
                  const value = detections[type.key];
                  return (
                    <Box key={type.key}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography fontWeight={600}>{type.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{type.desc}</Typography>
                        </Box>
                        <FormControlLabel
                          control={<Switch checked={value.enabled} disabled />}
                          label={value.enabled ? 'Enabled' : 'Disabled (set in Alarm Configuration)'}
                        />
                      </Stack>
                      <Slider
                        value={value.sensitivity}
                        onChange={(_, sensitivity) => setDetections(s => ({
                          ...s, [type.key]: { ...s[type.key], sensitivity },
                        }))}
                        min={1} max={10} step={1} marks={[{ value: 1, label: '1' }, { value: 10, label: '10' }]}
                        disabled={!value.enabled} valueLabelDisplay="auto" sx={{ color: type.color }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {value.enabled ? `Current: ${value.sensitivity}` : 'Currently disabled'}
                      </Typography>
                      {type.key === 'presence' && (
                        <Stack direction="row" spacing={2} mt={2} alignItems="center">
                          <TextField label="Away Threshold (minutes)" type="number" size="small"
                            value={value.away_threshold_minutes}
                            disabled={!value.enabled}
                            inputProps={{ min: 0.1, max: 240, step: 0.1 }}
                            onChange={e => setDetections(s => ({ ...s, presence: {
                              ...s.presence, away_threshold_minutes: Number(e.target.value),
                            }}))} />
                          <FormControlLabel label="Auto Close at Shift End"
                            control={<Switch checked={value.auto_close_at_shift_end}
                              disabled={!value.enabled}
                              onChange={e => setDetections(s => ({ ...s, presence: {
                                ...s.presence, auto_close_at_shift_end: e.target.checked,
                              }}))} />} />
                        </Stack>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>

          <Button variant="contained" size="large" startIcon={<SaveIcon />}
            onClick={save} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save All Settings'}
          </Button>

        </Stack>
      )}
    </Box>
  );
}
